<template>
  <section class="about-goal">
    <div class="about-goal-container">
      <div class="section-header">
        <span class="section-label">{{ sectionLabel }}</span>
        <h2 class="section-title">{{ sectionTitle }}</h2>
      </div>

      <div class="about-goal-content">
        <div class="about-goal-image-wrap">
          <img
            :src="image"
            :alt="sectionTitle"
            class="about-goal-image"
          >
        </div>
        <div class="about-goal-text">
          <div class="text-block">
            <h3 class="feature-title">{{ missionTitle }}</h3>
            <p class="feature-description">{{ missionText1 }}</p>
            <p class="feature-description">{{ missionText2 }}</p>
          </div>
          <div class="text-block">
            <h3 class="feature-title">{{ visionTitle }}</h3>
            <p class="feature-description">{{ visionText1 }}</p>
            <p class="feature-description">{{ visionText2 }}</p>
          </div>
        </div>
      </div>

      <div ref="statsRef" class="about-goal-stats">
        <div
          v-for="(stat, index) in stats"
          :key="index"
          class="stat-item"
        >
          <span class="stat-value">{{ displayedValues[index] }}{{ stat.suffix }}</span>
          <span class="stat-label">{{ stat.label }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
const { t } = useI18n()
const sectionLabel = computed(() => t('about.goal.label'))
const sectionTitle = computed(() => t('about.goal.title'))
const image = '/assets/images/about/group-237.png'

const missionTitle = computed(() => t('about.goal.missionTitle'))
const missionText1 = computed(() => t('about.goal.missionText1'))
const missionText2 = computed(() => t('about.goal.missionText2'))
const visionTitle = computed(() => t('about.goal.visionTitle'))
const visionText1 = computed(() => t('about.goal.visionText1'))
const visionText2 = computed(() => t('about.goal.visionText2'))

const { data: apiStats } = useAboutStats()

function formatStat(n: number): { target: number; suffix: string } {
  return n >= 1000
    ? { target: Math.floor(n / 1000), suffix: 'K+' }
    : { target: n, suffix: '+' }
}

const stats = computed(() => {
  const s = apiStats.value
  return [
    { ...formatStat(s?.courses   ?? 200),  label: t('about.goal.stats.totalCourses') },
    { ...formatStat(s?.contacts  ?? 27000), label: t('about.goal.stats.studentsEnrolled') },
    { ...formatStat(s?.missioners ?? 1200), label: t('about.goal.stats.certifiedTutors') },
    { ...formatStat(s?.graduates ?? 0),    label: t('about.goal.stats.graduates') },
  ]
})

const displayedValues = ref<number[]>([0, 0, 0, 0])
const statsRef = ref<HTMLElement | null>(null)

const DURATION = 1800 // мс

function easeOutQuart(t: number): number {
  return 1 - Math.pow(1 - t, 4)
}

function animateCountUp() {
  const startTime = performance.now()
  const targets = stats.value.map(s => s.target)

  function tick(now: number) {
    const elapsed = now - startTime
    const progress = Math.min(elapsed / DURATION, 1)
    const eased = easeOutQuart(progress)

    displayedValues.value = targets.map(t => Math.round(t * eased))

    if (progress < 1) {
      requestAnimationFrame(tick)
    }
  }

  requestAnimationFrame(tick)
}

onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting) {
        animateCountUp()
        observer.disconnect()
      }
    },
    { threshold: 0.3 },
  )

  if (statsRef.value) {
    observer.observe(statsRef.value)
  }
})
</script>

<style scoped>
/* Секция и контейнер — как AboutUsSection, ExpertsSection */
.about-goal {
  padding: 80px 0;
  background: #fff;
  max-width: var(--max-width-container);
  margin: 0 auto;
}

.about-goal-container {
  max-width: var(--max-width-container);
  margin: 0 auto;
  padding: 0 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Перевизначення загальних стилів секції (базові в index.css) */
.section-header {
  margin-bottom: 0px;
}

.section-title {
  font-size: 48px;
}

/* Контент: сетка как в ExpertsSection (две колонки) */
.about-goal-content {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: start;
  width: 100%;
  margin-bottom: 48px;
  gap: 30px;
  margin-top: 60px;

}

.about-goal-image-wrap {
  position: relative;
  /* border-radius: 16px; */
  overflow: hidden;
}

.about-goal-image-bg {
  position: absolute;
  inset: -20px;
  /* border-radius: 20px; */
  z-index: 0;
}

.about-goal-image {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 578px;
  /* aspect-ratio: 660/520; */
  display: block;
  /* border-radius: 12px; */
  object-fit: cover;
}

.about-goal-text {
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-top: -8px;
}

.text-block {
  text-align: left;
}

/* Заголовок и описание блока — из AboutUsSection (feature-title, feature-description) */
.feature-title {
  font-family: var(--font-inter);
  font-weight: 600;
  font-size: 34px;
  line-height: 1.3;
  color: var(--color-dark-blue);
  margin: 0 0 16px 0;
}

.feature-description {
  font-family: var(--font-inter);
  font-size: var(--font-size-body);
  line-height: var(--line-height-body);
  color: var(--color-grey);
  margin: 0;
}

.feature-description + .feature-description {
  margin-top: 10px;
}

/* Статистика — сетка как в ExpertsSection */
.about-goal-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  width: 100%;
  padding-top: 48px;
  border-top: 1px solid #E5E7EB;
}

.stat-item {
  text-align: center;
}

.stat-value {
  display: block;
  font-family: var(--font-inter);
  font-weight: 600;
  font-size: 52px;
  line-height: 1.2;
  color: var(--color-dark-blue);
  margin-bottom: 8px;
}

.stat-label {
  font-family: var(--font-inter);
  font-size: var(--font-size-body);
  color: var(--color-grey);
}

/* Медиазапросы — из AboutUsSection, ExpertsSection */
@media (max-width: 1400px) {
  .about-goal-container {
    padding: 0 60px;
  }
}

@media (max-width: 991px) {
  .about-goal {
    padding: 60px 0;
  }

  .about-goal-container {
    padding: 0 30px;
  }

  .section-title {
    font-size: 36px;
  }

  .about-goal-content {
    grid-template-columns: 1fr;
    gap: 32px;
    margin-bottom: 40px;
    margin-top: 40px;
  }

  .about-goal-image-wrap {
    max-width: 480px;
    margin: 0 auto;
  }

  .about-goal-stats {
    grid-template-columns: repeat(2, 1fr);
    gap: 32px;
    padding-top: 40px;
  }
}

@media (max-width: 767px) {
  .about-goal {
    padding: 40px 0;
  }

  .about-goal-container {
    padding: 0 20px;
  }

  .section-title {
    font-size: 32px;
  }

  .about-goal-stats {
    gap: 24px;
    padding-top: 32px;
  }

  .stat-value {
    font-size: 28px;
  }
}

@media (max-width: 575px) {
  .section-header {
    margin-bottom: 40px;
  }

  .section-title {
    font-size: 35px;
  }

  .feature-title {
    font-size: var(--font-size-title);
  }

  .feature-description {
    font-size: var(--font-size-body);
  }
}
</style>
